import { useMemo, useState } from "react";
import { motion } from "motion/react";
import { Calendar, User, ArrowRight, Search } from "lucide-react";
import { Link } from "react-router";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { blogPosts } from "../data/blogs";

export function Blog() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const categories = ["All", ...Array.from(new Set(blogPosts.map((post) => post.category)))];

  const filteredStories = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchesCategory =
        selectedCategory === "all" || post.category.toLowerCase() === selectedCategory.toLowerCase();
      const matchesSearch =
        searchTerm.trim().length === 0 ||
        [post.title, post.excerpt, post.author, post.category]
          .join(" ")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      return matchesCategory && matchesSearch;
    });
  }, [searchTerm, selectedCategory]);

  return (
    <div className="bg-background">
      <section className="relative overflow-hidden py-28 md:py-32 text-primary-foreground">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,_rgba(45,95,76,0.98),_rgba(45,95,76,0.82))]" />
        <div className="absolute inset-0 opacity-35 [background-image:radial-gradient(circle_at_top_right,_rgba(212,184,150,0.5),_transparent_28%),radial-gradient(circle_at_bottom_left,_rgba(139,157,131,0.3),_transparent_28%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <h1 className="text-3xl md:text-4xl mb-6 font-bold tracking-tight">
              Stories, updates, and field notes.
            </h1>
            {/*  <h1 className="text-3xl md:text-4xl mb-6 font-bold">
              Choose Your Impact
            </h1>
            <p className="text-xl md:text-2xl opacity-95 leading-relaxed"> */}
            <p className="text-xl md:text-2xl opacity-90 leading-relaxed max-w-3xl">
              Read the latest posts from Immerse Foundation, with article pages that support rich text, images, and embedded video.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-8 bg-white border-b border-border sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={
                    selectedCategory === category.toLowerCase() || (selectedCategory === "all" && category === "All")
                      ? "default"
                      : "outline"
                  }
                  onClick={() => setSelectedCategory(category.toLowerCase())}
                >
                  {category}
                </Button>
              ))}
            </div>

            <div className="relative w-full lg:w-[320px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search posts"
                className="h-11 w-full rounded-full border border-border bg-background pl-11 pr-4 text-sm outline-none transition-colors focus:border-primary"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {filteredStories.map((story, index) => (
              <motion.div
                key={story.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="h-full"
              >
                <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-300 h-full flex flex-col border-border/70">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={story.coverImage}
                      alt={story.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <CardContent className="px-6 pb-6 pt-0 flex flex-col flex-1 bg-white">
                    <Badge variant="secondary" className="mb-3 w-fit">
                      {story.category}
                    </Badge>
                    <h3 className="text-2xl mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                      {story.title}
                    </h3>
                    <p className="text-muted-foreground mb-5 line-clamp-3 flex-1 leading-relaxed">
                      {story.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-5">
                      <div className="flex items-center gap-1">
                        <User size={14} />
                        <span>{story.author}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        <span>
                          {new Date(story.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                    </div>
                    <Link to={`/news/${story.slug}`}>
                      <Button variant="ghost" className="w-full justify-between group px-0 hover:bg-transparent hover:text-primary">
                        Read More
                        <ArrowRight
                          className="transition-transform group-hover:translate-x-1"
                          size={18}
                        />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {filteredStories.length === 0 && (
            <div className="text-center py-20">
              <p className="text-2xl text-muted-foreground">
                No blog posts found for this filter.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
